import { useState, useEffect } from 'react';
import { X, Phone, MessageSquare, Users, FileText, MoreHorizontal, Info } from 'lucide-react';
import { useActivityStore } from '../../store/activityStore';
import { PersonPicker } from '../shared/PersonPicker';
import type { ActivityType, ScheduleActivityCreate } from '../../types';
import toast from 'react-hot-toast';

interface ScheduleActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  personId?: string;
  personName?: string;
  onScheduled?: () => void;
  assignmentNudge?: {
    variant: 'assigned-to-other' | 'unassigned';
    assigneeName?: string;
  } | null;
}

// Activity types with icons (no EMAIL for scheduling)
const ACTIVITY_TYPES: { type: ActivityType; label: string; icon: typeof Phone }[] = [
  { type: 'CALL', label: 'Call', icon: Phone },
  { type: 'TEXT', label: 'Text', icon: MessageSquare },
  { type: 'IN_PERSON', label: 'In Person', icon: Users },
  { type: 'NOTE', label: 'Note', icon: FileText },
  { type: 'OTHER', label: 'Other', icon: MoreHorizontal },
];

// Helper for default date — tomorrow at 9:00 AM local time
const getTomorrowMorning = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  const offset = tomorrow.getTimezoneOffset();
  const local = new Date(tomorrow.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

/**
 * Modal for scheduling a future activity against a person
 */
export const ScheduleActivityModal = ({
  isOpen,
  onClose,
  personId,
  personName,
  onScheduled,
  assignmentNudge,
}: ScheduleActivityModalProps) => {
  const { scheduleActivity, isSaving, saveError, clearSaveError } = useActivityStore();

  // Person picker state (used when personId is not provided)
  const [selectedPerson, setSelectedPerson] = useState<{ id: string; firstName: string; lastName: string } | null>(null);
  const effectivePersonId = personId || selectedPerson?.id;
  const effectivePersonName = personName || (selectedPerson ? `${selectedPerson.firstName} ${selectedPerson.lastName}` : '');

  // Form state
  const [activityType, setActivityType] = useState<ActivityType>('CALL');
  const [scheduledAt, setScheduledAt] = useState('');
  const [summary, setSummary] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setActivityType('CALL');
      setScheduledAt(getTomorrowMorning());
      setSummary('');
      setSelectedPerson(null);
      clearSaveError();
    }
  }, [isOpen, clearSaveError]);

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!effectivePersonId) {
      toast.error('Please select a person');
      return;
    }

    if (!scheduledAt) {
      toast.error('Please select a date and time');
      return;
    }

    try {
      const data: ScheduleActivityCreate = {
        type: activityType,
        scheduledAt: new Date(scheduledAt).toISOString(),
        status: 'PLANNED',
        ...(summary.trim() && { summary: summary.trim() }),
      };

      await scheduleActivity(effectivePersonId, data);

      toast.success('Activity scheduled');
      onScheduled?.();
      onClose();
    } catch (error) {
      // Error already shown via store
      console.error('Failed to schedule activity:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Schedule Activity
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Person picker (when no personId provided) or static person name */}
              {!personId ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Person <span className="text-red-500">*</span>
                  </label>
                  <PersonPicker
                    value={selectedPerson}
                    onChange={setSelectedPerson}
                    placeholder="Search for a person..."
                    required
                  />
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  For: <span className="font-medium text-gray-900 dark:text-gray-100">{effectivePersonName}</span>
                </p>
              )}

              {assignmentNudge && (
                <div className="flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-200">
                  <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>
                    {assignmentNudge.variant === 'assigned-to-other'
                      ? `This contact is assigned to ${assignmentNudge.assigneeName ?? 'another rep'}.`
                      : 'This contact has no assigned rep.'}
                  </span>
                </div>
              )}

              {/* Error banner */}
              {saveError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm">
                  {saveError}
                </div>
              )}

              {/* Activity Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Activity Type
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {ACTIVITY_TYPES.map(({ type, label, icon: Icon }) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setActivityType(type)}
                      className={`flex flex-col items-center justify-center px-2 py-3 rounded-lg border-2 transition-all ${
                        activityType === type
                          ? 'border-blast-600 bg-blast-50 text-blast-700 dark:border-blast-400 dark:bg-blast-950 dark:text-blast-400'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600 dark:border-gray-600 dark:hover:border-gray-500 dark:text-gray-400'
                      }`}
                    >
                      <Icon className="w-5 h-5 mb-1" />
                      <span className="text-xs font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Date/Time */}
              <div>
                <label htmlFor="scheduledAt" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  id="scheduledAt"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="input"
                  required
                />
              </div>

              {/* Summary (optional) */}
              <div>
                <label htmlFor="summary" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Summary <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="summary"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  rows={3}
                  placeholder="What's the plan?"
                  className="input"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSaving || !scheduledAt || !effectivePersonId}
              >
                {isSaving ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Saving...
                  </>
                ) : (
                  'Schedule Activity'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
